import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/hooks/useAuthContext';
import { useNotificationsAPI, useNotifications, useWebSocket, enrichLegacyNotification } from '@/hooks/useWebSocket';
import { getDisplayTitle, getDisplayMessage, formatRelativeTime } from '@/lib/notification-display';
import { Card, CardContent } from '@/components/ui';
import { LoadingSpinner } from '@/components/common';

type FilterMode = 'all' | 'unread' | 'read';
const PAGE_SIZE = 20;

export default function Notifications() {
  const auth = useAuthContext();
  const { user: _user } = auth;
  const token = ('token' in auth ? auth.token : undefined) || null;
  const navigate = useNavigate();
  const { unreadCount } = useWebSocket(token);
  const {
    notifications,
    total,
    loading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    setNotifications,
  } = useNotificationsAPI(token);

  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [page, setPage] = useState(0);

  // Fetch notifications when page or filter changes
  useEffect(() => {
    const unreadOnly = filterMode === 'unread';
    fetchNotifications(PAGE_SIZE, page * PAGE_SIZE, unreadOnly);
  }, [page, filterMode, fetchNotifications]);

  // Reset to first page when filter changes
  const handleFilterChange = (mode: FilterMode) => {
    setFilterMode(mode);
    setPage(0);
  };

  // Listen for new real-time notifications
  useNotifications(
    useCallback((notification) => {
      const enriched = enrichLegacyNotification(notification);
      setNotifications((prev) => {
        if (prev.some((n) => n.id === enriched.id)) return prev;
        return [enriched, ...prev];
      });
    }, [setNotifications])
  );

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
    const unreadOnly = filterMode === 'unread';
    fetchNotifications(PAGE_SIZE, page * PAGE_SIZE, unreadOnly);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
    const unreadOnly = filterMode === 'unread';
    fetchNotifications(PAGE_SIZE, page * PAGE_SIZE, unreadOnly);
  };

  const handleNotificationClick = (notification: typeof notifications[0]) => {
    if (notification.relatedLineItemId) {
      navigate(`/items/${notification.relatedLineItemId}`);
    } else if (notification.relatedHoldRequestId) {
      navigate(`/holds/${notification.relatedHoldRequestId}`);
    } else {
      setExpandedId(expandedId === notification.id ? null : notification.id);
    }
  };

  // For "read" filter, we fetch all and filter client-side since the API only supports unreadOnly
  const displayNotifications = filterMode === 'read'
    ? notifications.filter((n) => n.isRead)
    : notifications;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatType = (type: string) =>
    type.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Notifications</h1>
          <p className="text-sm text-neutral-400 mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        {notifications.some((n) => !n.isRead) && (
          <button
            onClick={handleMarkAllAsRead}
            className="px-4 py-2 text-sm font-medium text-outfront-purple bg-outfront-purple-light rounded-lg hover:bg-outfront-purple-light transition-colors"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-4 bg-neutral-100 rounded-lg p-1 w-fit">
        {(['all', 'unread', 'read'] as FilterMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => handleFilterChange(mode)}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filterMode === mode
                ? 'bg-white text-neutral-900 '
                : 'text-neutral-400 hover:text-neutral-600'
            }`}
          >
            {mode.charAt(0).toUpperCase() + mode.slice(1)}
            {mode === 'unread' && unreadCount > 0 && (
              <span className="ml-1.5 px-1.5 py-0.5 text-xs bg-red-100 text-red-700 rounded-full">
                {unreadCount}
              </span>
            )}
          </button>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && notifications.length === 0 ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : error ? (
            <div className="py-16 text-center">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => {
                  const unreadOnly = filterMode === 'unread';
                  fetchNotifications(PAGE_SIZE, page * PAGE_SIZE, unreadOnly);
                }}
                className="mt-2 text-sm text-outfront-purple hover:text-outfront-purple"
              >
                Try again
              </button>
            </div>
          ) : displayNotifications.length === 0 ? (
            <div className="py-16 text-center text-neutral-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-neutral-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-sm">
                {filterMode === 'unread' ? 'No unread notifications' : filterMode === 'read' ? 'No read notifications' : 'No notifications yet'}
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-neutral-100">
              {displayNotifications.map((notification) => {
                const displayTitle = getDisplayTitle(notification);
                const displayMessage = getDisplayMessage(notification);
                const isExpanded = expandedId === notification.id;
                const notificationType = ('notificationType' in notification ? (notification as { notificationType: string }).notificationType : '') || ('type' in notification ? (notification as { type: string }).type : '') || '';

                return (
                  <li
                    key={notification.id}
                    className={`px-5 py-4 transition-colors hover:bg-neutral-50 ${
                      !notification.isRead ? 'bg-outfront-purple-light/50 border-l-3 border-l-outfront-purple' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Unread dot */}
                      <div className="pt-1.5 w-3 flex-shrink-0">
                        {!notification.isRead && (
                          <span className="block w-2.5 h-2.5 rounded-full bg-outfront-purple" />
                        )}
                      </div>

                      {/* Content */}
                      <div
                        className="flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-center gap-2">
                          <h3 className={`text-sm truncate ${!notification.isRead ? 'font-semibold text-neutral-900' : 'font-medium text-neutral-600'}`}>
                            {displayTitle}
                          </h3>
                          {notificationType && (
                            <span className="flex-shrink-0 text-[10px] px-1.5 py-0.5 bg-neutral-100 text-neutral-400 rounded">
                              {formatType(notificationType)}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-neutral-600 mt-0.5 line-clamp-2">{displayMessage}</p>
                        <p className="text-xs text-neutral-400 mt-1">{formatRelativeTime(notification.createdAt)}</p>

                        {/* Expanded detail view */}
                        {isExpanded && (
                          <div className="mt-3 p-3 bg-neutral-50 rounded-lg border border-neutral-200 text-sm">
                            <p className="font-medium text-neutral-900 mb-1">{notification.title}</p>
                            <p className="text-neutral-600 whitespace-pre-wrap">{notification.message}</p>
                            {notification.relatedHoldRequestId && (
                              <p className="mt-2 text-xs text-neutral-400">
                                Hold Request: <span className="text-outfront-purple">{notification.relatedHoldRequestId}</span>
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex-shrink-0 flex items-center gap-2">
                        {!notification.isRead && (
                          <button
                            onClick={() => handleMarkAsRead(notification.id)}
                            className="text-xs text-outfront-purple hover:text-outfront-purple whitespace-nowrap"
                            title="Mark as read"
                          >
                            Mark read
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-sm text-neutral-400">
            Showing {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <span className="text-sm text-neutral-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1 || loading}
              className="px-3 py-1.5 text-sm font-medium rounded-md border border-neutral-200 text-neutral-600 hover:bg-neutral-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
